import React, { useState, useRef } from 'react';
import PopupMenu from '../Popup/PopupMenu';
import { useUserViews } from '../../hooks/useQueries';
import { useIsMobile } from '../../hooks/useIsMobile';
import './ViewDropdown.css';

export default function ViewDropdown({ 
  tableName, 
  currentView, 
  onViewChange, 
  onConfigureView,
  onCreateView,
  onEditView,
  onDeleteView,
  onSetDefaultView
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const dropdownRef = useRef(null);
  const isMobile = useIsMobile();
  
  const { data: userViews = [], isLoading, error } = useUserViews(tableName);

  // Label for the built-in "show everything" view, and whether any user view is the
  // current landing default. When none is, the built-in view is the effective default.
  const defaultViewLabel = tableName === 'tasks' ? 'All Tasks' : 'All Items';

  // Menu heading: singular, capitalized form of the table name ("tasks" -> "Task Views").
  const singularTableName = String(tableName || '').replace(/s$/, '');
  const menuTitle = `${singularTableName.charAt(0).toUpperCase()}${singularTableName.slice(1)} Views`;
  const hasUserDefault = userViews.some(view => view?.is_default === true);

  const handleDropdownClick = (e) => {
    // Check if the click is on a pill/tag element or within a React Select component
    const clickedElement = e.target;
    const isInteractiveChild = 
      clickedElement.closest('.react-select__multi-value') ||
      clickedElement.closest('.react-select__multi-value__remove') ||
      clickedElement.closest('.react-select__clear-indicator') ||
      clickedElement.closest('a') ||
      clickedElement.closest('button:not(.view-dropdown-button)') ||
      clickedElement.closest('[data-prevent-dropdown]');
    
    // If clicking on an interactive child element, don't open dropdown
    if (isInteractiveChild) {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();

      const padding = 12;
      const rawX = rect.left + rect.width / 2;
      const rawY = rect.bottom + 5;

      const clampedX = Math.max(padding, Math.min(rawX, window.innerWidth - padding));
      const clampedY = Math.max(padding, Math.min(rawY, window.innerHeight - padding));

      setMenuPosition({
        x: clampedX,
        y: clampedY
      });
    }
    setIsOpen(true);
  };

  const handleCloseMenu = () => {
    setIsOpen(false);
  };

  const handleViewSelect = (view) => {
    onViewChange(view);
    handleCloseMenu();
  };

  const getSelectedUserView = () => {
    if (!currentView || currentView === 'default') {
      return null;
    }

    return userViews.find(view => view.id === parseInt(currentView, 10)) || null;
  };

  const isSystemView = (view) => {
    const name = String(view?.view_name || '');
    return name === 'My Tasks' || name === 'My Items';
  };

  // Global views belong to everyone; only admins may change them. The server sets
  // can_edit per requesting user and re-checks it on every write.
  const isGlobalView = (view) => view?.is_global === true;
  const canEditView = (view) => !!view && view.can_edit !== false && !isSystemView(view);

  const getDisplayName = () => {
    if (isLoading) return 'Loading...';
    if (error) return 'Error loading views';
    
    if (!currentView || currentView === 'default') {
      return defaultViewLabel;
    }

    // Find the current view in user views - handle both string and number IDs
    const selectedView = userViews.find(view => view.id === parseInt(currentView));
    return selectedView ? selectedView.view_name : 'Unknown View';
  };

  const buildMenuItems = () => {
    const items = [];
    const isDefaultSelected = currentView === 'default';
    const defaultIconGroup = (
      <span className="view-menu-icon-group">
        {isDefaultSelected && <i className="bi bi-check"></i>}
        {!hasUserDefault && <i className="bi bi-bookmark-star-fill view-default-icon" title="Default view"></i>}
        <i className="bi bi-layers-fill view-system-icon" title="System view"></i>
      </span>
    );

    // Built-in "show everything" view option
    items.push({
      label: defaultViewLabel,
      onClick: () => handleViewSelect('default'),
      icon: defaultIconGroup
    });

    // User custom views, plus any global views shared with this user
    userViews.forEach(view => {
      const isSelected = parseInt(currentView, 10) === view.id;
      const protectedSystemView = isSystemView(view);
      const globalView = isGlobalView(view);
      const globalTitle = globalView
        ? (view.owner_name ? `Global view (created by ${view.owner_name})` : 'Global view')
        : null;
      const iconGroup = (
        <span className="view-menu-icon-group">
          {isSelected && <i className="bi bi-check"></i>}
          {view.is_default === true && <i className="bi bi-bookmark-star-fill view-default-icon" title="Default view"></i>}
          {globalView && <i className="bi bi-globe2 view-global-icon" title={globalTitle}></i>}
          {protectedSystemView && <i className="bi bi-layers-fill view-system-icon" title="System view"></i>}
        </span>
      );

      items.push({
        label: view.view_name,
        onClick: () => handleViewSelect(view.id),
        icon: iconGroup,
        rightAction: (canEditView(view) && onConfigureView)
          ? {
              icon: 'bi bi-gear',
              title: 'Edit view',
              ariaLabel: `Edit ${view.view_name}`,
              onClick: () => {
                handleViewSelect(view.id);
                onConfigureView(view);
              }
            }
          : null
      });
    });

    // Add separator before actions if we have views
    if (items.length > 0) {
      items.push({
        label: '─────────────────',
        disabled: true,
        onClick: () => {}
      });
    }

    // Configure View button. Hidden for system views and for global views the
    // current user is not allowed to change.
    if (currentView && currentView !== 'default' && onConfigureView) {
      const selectedView = userViews.find(view => view.id === parseInt(currentView));

      if (canEditView(selectedView)) {
        items.push({
          labelNode: (
            <>
              <i className="bi bi-gear" style={{ marginRight: 8 }}></i>
              Configure View
            </>
          ),
          onClick: onConfigureView
        });
      }
    }

    // Action buttons
    if (onCreateView) {
      items.push({
        labelNode: (
          <>
            <i className="bi bi-plus-circle" style={{ marginRight: 8 }}></i>
            Create New View
          </>
        ),
        onClick: onCreateView
      });
    }

    // Make the currently selected view the default, shown whenever it is not
    // already the landing default. This includes the built-in view: selecting it as
    // default just clears the flag from whichever user view currently holds it.
    if (onSetDefaultView) {
      const makeDefaultItem = (target) => ({
        labelNode: (
          <>
            <i className="bi bi-bookmark-star" style={{ marginRight: 8 }}></i>
            Make this view default
          </>
        ),
        onClick: () => {
          onSetDefaultView(target);
          handleCloseMenu();
        }
      });

      if (currentView === 'default') {
        // Built-in view is the default only when no user view is flagged.
        if (hasUserDefault) {
          items.push(makeDefaultItem('default'));
        }
      } else {
        const selectedView = userViews.find(view => view.id === parseInt(currentView, 10));
        if (selectedView && selectedView.is_default !== true) {
          items.push(makeDefaultItem(selectedView));
        }
      }
    }

    // Delete only available for views this user is allowed to change
    if (currentView && currentView !== 'default') {
      const selectedView = userViews.find(view => view.id === parseInt(currentView));

      if (onDeleteView && canEditView(selectedView)) {
        items.push({
          labelNode: (
            <>
              <i className="bi bi-trash" style={{ marginRight: 8 }}></i>
              Delete View
            </>
          ),
          onClick: () => onDeleteView(selectedView),
          color: '#dc2626'
        });
      }
    }

    return items;
  };

  if (!tableName) {
    return null;
  }

  const selectedUserView = getSelectedUserView();
  const builtInIsDefault = currentView === 'default' && !hasUserDefault;
  const isSelectedViewDefault = selectedUserView?.is_default === true || builtInIsDefault;
  const isSelectedSystemView = currentView === 'default' || isSystemView(selectedUserView);
  const isSelectedGlobalView = isGlobalView(selectedUserView);

  return (
    <div className="view-dropdown">
      <button
        ref={dropdownRef}
        className="view-dropdown-button"
        onClick={handleDropdownClick}
        disabled={isLoading}
        data-popup-trigger="view-dropdown"
      >
        <span className="view-dropdown-label">
          View: {getDisplayName()}
          {isSelectedViewDefault && (
            <i className="bi bi-bookmark-star-fill view-dropdown-default-marker" title="Default view"></i>
          )}
          {isSelectedGlobalView && (
            <i className="bi bi-globe2 view-dropdown-global-marker" title="Global view"></i>
          )}
          {isSelectedSystemView && (
            <i className="bi bi-layers-fill view-dropdown-system-marker" title="System view"></i>
          )}
        </span>
        <span className={`view-dropdown-arrow ${isOpen ? 'open' : ''}`}>
          ▼
        </span>
      </button>
      
      {isOpen && (
        <PopupMenu
          x={menuPosition.x}
          y={menuPosition.y}
          onClose={handleCloseMenu}
          title={menuTitle}
          items={buildMenuItems()}
          minWidth={isMobile ? 240 : 200}
        />
      )}
    </div>
  );
}